import React, { useState, useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Box,
} from '@chakra-ui/react';
import { MdEdit, MdOutlineDelete } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import * as api from '../../../../api/noticia';

interface Noticia {
  seqConteudo: number;
  nomePublicacao: string;
  dscSituacaoPublicacao: string;
  datRegistro: string;
  datPublicacao: string;
  datDespublicar: string;
}

interface TabelaNoticiaProps {
  listaNoticia: Noticia[];
  listaVazia: string;
  loading: boolean;
  onDelete: (seqMidia: number) => void;
}

type Ordenacao = { chave: keyof Noticia; direcao: 'asc' | 'desc' } | null;

const TabelaNoticia = ({ 
  listaNoticia, 
  listaVazia, 
  loading,
  onDelete
}: TabelaNoticiaProps) => {
  const navigate = useNavigate();
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof Noticia | null>(null);

  function deletarPublicacao(seqPublicacao: number) {
    Swal.fire({
      title: 'Tem certeza?',
      text: "Certeza que deseja deletar esta publicação?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1789FC',
      cancelButtonColor: '#EDF2F7',
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'info',
          title: 'Aguarde',
          allowOutsideClick: false,
          text: 'Deletando...',
          confirmButtonColor: '#1789FC',
          confirmButtonText: 'OK',
      })
          try {
            const obterNoticia = await api.obterNoticia(seqPublicacao);
            if(obterNoticia.binPublicacao) {
              const delimiter = "/cms-dev-files/";
              const index = obterNoticia.binPublicacao.lastIndexOf(delimiter);
              const stringFinalDaUrlCdn =
                index !== -1 ? obterNoticia.binPublicacao.substring(index + delimiter.length) : obterNoticia.binPublicacao;
              await api.deletarNoticiaNoCdn(stringFinalDaUrlCdn);
            }
          } catch (error) {
            console.error("Erro ao obter publicação:", error);
          }
          try {
            await api.deletarNoticia(seqPublicacao).then(() => {
              onDelete(seqPublicacao)
              Swal.fire({
                icon: 'success',
                title: 'Sucesso!',
              text: 'Publicação deletada com sucesso.',
              confirmButtonColor: '#1789FC',
              confirmButtonText: 'OK',
            })
          });
        } catch (error) {
          console.error('Erro ao deletar publicação:', error);
          Swal.fire({
            icon: 'error',
            title: 'Ocorreu um erro!',
            text: 'Erro ao deletar publicação. Tente novamente mais tarde.',
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate(-1)
          });
        }
      }
    });
  }

  const manipularOrdenacao = (chave: keyof Noticia) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaNoticia];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        const valorA = a[ordenacao.chave];
        const valorB = b[ordenacao.chave];
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaNoticia, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof Noticia) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const limitarTexto = (texto: string, tamanhoMaximo: number) => {
    return texto.length > tamanhoMaximo ? texto.substring(0, tamanhoMaximo) + '...' : texto;
  };

  const renderizarLinhas = () =>
    listaOrdenada.length > 0 ? (
      listaOrdenada.map((noticia) => (
        <Tr key={noticia.seqConteudo}>
          <Td>{limitarTexto(noticia.nomePublicacao, 30)}</Td>
          <Td>{noticia.dscSituacaoPublicacao}</Td>
          <Td>{noticia.datRegistro.split('-').reverse().join('/')}</Td>
          <Td>{noticia.datPublicacao.split('-').reverse().join('/')}</Td>
          <Td>{noticia.datDespublicar.split('-').reverse().join('/')}</Td>
          <Td>
            <IconButton
              aria-label="Botão Editar"
              bgColor="#fff"
              onClick={() => navigate(`editar-noticia/${noticia.seqConteudo}`)}
            >
              <Icon as={MdEdit} fontSize="24px" color="#1789FC" />
            </IconButton>
          </Td>
          <Td>
            <IconButton
              aria-label="Botão deletar"
              bgColor="#fff"
              _hover={{ bg: '#ff00002b' }}
              onClick={() => deletarPublicacao(noticia.seqConteudo)}
            >
              <Icon as={MdOutlineDelete} fontSize="24px" color="red"/>
            </IconButton>
          </Td>
        </Tr>
      ))
    ) : (
      <Tr>
        <Td colSpan={6} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('nomePublicacao')}
              onMouseEnter={() => setColunaDestacada('nomePublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'nomePublicacao' ? 'blue.500' : 'inherit'}>
                Título da notícia {obterIndicadorOrdenacao('nomePublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('dscSituacaoPublicacao')}
              onMouseEnter={() => setColunaDestacada('dscSituacaoPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'dscSituacaoPublicacao' ? 'blue.500' : 'inherit'}>
                Situação {obterIndicadorOrdenacao('dscSituacaoPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datRegistro' ? 'blue.500' : 'inherit'}>
                Data do Registro {obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datPublicacao')}
              onMouseEnter={() => setColunaDestacada('datPublicacao')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datPublicacao' ? 'blue.500' : 'inherit'}>
                Data Para Publicação {obterIndicadorOrdenacao('datPublicacao')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: '#1468C9' }}
              onClick={() => manipularOrdenacao('datDespublicar')}
              onMouseEnter={() => setColunaDestacada('datDespublicar')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
            >
              <Box display="flex" alignItems="center" color={ordenacao?.chave === 'datDespublicar' ? 'blue.500' : 'inherit'}>
                Data Fim da Publicação {obterIndicadorOrdenacao('datDespublicar')}
              </Box>
            </Th>
            <Th>Editar</Th>
            <Th>Deletar</Th>
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr>
              <Td colSpan={6}>
                <Center>
                  <Spinner size="lg" color="blue.500" />
                </Center>
              </Td>
            </Tr>
          ) : (
            renderizarLinhas()
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaNoticia;
